import React, { useState } from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Sparkles, Loader2, CheckCircle2 } from 'lucide-react';
import { cancelSubscription, resumeSubscription } from '../lib/api';
import { toast } from 'sonner';

const PLAN_LABEL = { weekly: 'Weekly ($5.99/wk)', monthly: 'Monthly ($9.99/mo)', yearly: 'Yearly ($42.99/yr)', trial: 'Free trial', free: 'Free' };

const SubscriptionCard = ({ user, onChange }) => {
  const [busy, setBusy] = useState(false);
  const sub = user?.subscription;
  const plan = user?.plan || 'free';
  const cancelling = !!sub?.cancel_at_period_end;
  const periodEnd = sub?.current_period_end ? new Date(sub.current_period_end * 1000).toLocaleDateString('en-US', { month:'long', day:'numeric', year:'numeric' }) : null;
  const trialEnd = user?.trial_ends_at ? new Date(user.trial_ends_at).toLocaleDateString('en-US', { month:'long', day:'numeric' }) : null;

  const handleCancel = async () => {
    setBusy(true);
    try { await cancelSubscription(); toast.success('Subscription will end at the current period end. You’ll keep access until then.'); onChange && onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel'); }
    finally { setBusy(false); }
  };
  const handleResume = async () => {
    setBusy(true);
    try { await resumeSubscription(); toast.success('Subscription resumed — welcome back!'); onChange && onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to resume'); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6 rounded-3xl border-emerald-950/5 bg-white">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-emerald-950/50 text-xs uppercase tracking-widest">Your plan</p>
          <h3 className="font-display text-xl font-bold text-emerald-950">{PLAN_LABEL[plan] || plan}</h3>
        </div>
        {plan === 'trial' && trialEnd && <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-semibold">Trial ends {trialEnd}</span>}
        {sub && !cancelling && <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-semibold flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Active</span>}
        {cancelling && <span className="text-xs px-2.5 py-1 rounded-full bg-orange-100 text-orange-700 font-semibold">Cancelling</span>}
      </div>
      {cancelling && periodEnd && (<p className="text-sm text-emerald-950/70 mb-3">Your subscription ends on <b>{periodEnd}</b>. You keep full access until then.</p>)}
      {sub && !cancelling && periodEnd && (<p className="text-sm text-emerald-950/70 mb-3">Renews automatically on <b>{periodEnd}</b>.</p>)}

      <div className="flex gap-2">
        {sub && !cancelling && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="border-emerald-950/10 rounded-full" disabled={busy}>Cancel subscription</Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-2xl">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display">Cancel your subscription?</AlertDialogTitle>
                <AlertDialogDescription>You’ll keep full access until <b>{periodEnd}</b>. After that, you’ll go back to the free plan (3 scans/day). No refund needed — no future charges.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep subscription</AlertDialogCancel>
                <AlertDialogAction onClick={handleCancel} className="bg-red-500 hover:bg-red-600">Yes, cancel</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        {sub && cancelling && (
          <Button onClick={handleResume} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Resume subscription</Button>
        )}
        {!sub && plan !== 'trial' && (
          <Button asChild className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full"><a href="/pricing"><Sparkles className="w-4 h-4 mr-2" />Upgrade to Pro</a></Button>
        )}
        {!sub && plan === 'trial' && (
          <Button asChild className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full"><a href="/pricing"><Sparkles className="w-4 h-4 mr-2" />Choose a plan</a></Button>
        )}
      </div>
    </Card>
  );
};

export default SubscriptionCard;
